// The overview's warmup curve (UI-8): what every pool sent each whole day,
// stacked — one soft-gradient layer per pool in its fixed colour — under the
// dashed line of the plan, on a linear axis. "Ma" marks today; the days after
// it are a faint band the plan runs on alone. A hover opens a popover with
// each pool's day, the total, and the plan.
//
// The total of the layers is the overview's own all-pools figure: each layer
// is the same day of the same pool the warmup page shows.

import { useId, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import { useDrawDuration } from "@/components/emc/charts/motion";
import { ChartPopover, CHART, poolColor } from "@/components/emc/charts/theme";

export interface StackDay {
  /** The x-axis text. */
  label: string;
  /** The plan for all the pools drawn; null where there is none. */
  plan: number | null;
  /** What went out, all pools; null for today and the future. */
  total: number | null;
  /** Each pool's whole day; null where it has none. */
  pools: Record<string, number | null>;
}

interface Props {
  days: StackDay[];
  /** The pools, bottom layer first. */
  pools: string[];
  /** Index of today in `days`, if it is in the window. */
  todayIndex: number | null;
  formatValue: (value: number) => string;
  /** A pool's note in the popover: "megállt". */
  poolNote?: (code: string) => string | null;
  ariaLabel: string;
  className?: string;
}

interface TooltipEntry {
  dataKey?: unknown;
  value?: unknown;
  payload?: Record<string, unknown>;
}

export function PoolStack({ days, pools, todayIndex, formatValue, poolNote, ariaLabel, className }: Props) {
  const duration = useDrawDuration();
  const [drawn, setDrawn] = useState(false);
  const animate = duration > 0 && !drawn;
  const titleId = useId();
  const prefix = useId().replace(/[^a-zA-Z0-9]/g, "");

  const rows = days.map((day) => ({ label: day.label, plan: day.plan, total: day.total, ...day.pools }));
  const lastIndex = (() => {
    for (let index = rows.length - 1; index >= 0; index -= 1) {
      if (typeof rows[index]?.total === "number") return index;
    }
    return -1;
  })();
  const last = lastIndex >= 0 ? rows[lastIndex] : undefined;
  const top = [...pools].reverse().find((code) => typeof last?.[code as keyof typeof last] === "number");
  const today = todayIndex !== null ? rows[todayIndex] : undefined;
  const end = rows[rows.length - 1];
  const hasPlan = rows.some((row) => typeof row.plan === "number");

  return (
    <figure className="m-0 flex flex-col gap-3" aria-labelledby={titleId}>
      <figcaption id={titleId} className="sr-only">
        {ariaLabel}
      </figcaption>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-label font-medium" aria-hidden="true">
        {pools.map((code, index) => (
          <span key={code} className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-flag" style={{ background: poolColor(code, index) }} />
            {code}
          </span>
        ))}
        {hasPlan && (
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="w-4 border-t-[1.5px] border-dashed border-muted-foreground" />
            terv
          </span>
        )}
        <span className="ml-auto text-muted-foreground">lineáris skála</span>
      </div>
      <div className={cn("h-72 w-full", className)}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 18, right: 4, bottom: 0, left: 4 }}>
            <defs>
              {pools.map((code, index) => (
                <linearGradient key={code} id={`${prefix}-${code}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" style={{ stopColor: poolColor(code, index), stopOpacity: 0.42 }} />
                  <stop offset="1" style={{ stopColor: poolColor(code, index), stopOpacity: 0.08 }} />
                </linearGradient>
              ))}
              <linearGradient id={`${prefix}-future`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" style={{ stopColor: CHART.series, stopOpacity: 0.07 }} />
                <stop offset="1" style={{ stopColor: CHART.series, stopOpacity: 0 }} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={CHART.grid} />
            {today && end && (
              <ReferenceArea x1={today.label} x2={end.label} fill={`url(#${prefix}-future)`} ifOverflow="visible" />
            )}
            <XAxis
              dataKey="label"
              axisLine={{ stroke: CHART.axis }}
              tickLine={false}
              tick={CHART.tick}
              tickMargin={8}
              minTickGap={28}
            />
            <YAxis
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={CHART.tick}
              tickFormatter={(value: number) => formatValue(value)}
              width="auto"
            />
            <Tooltip
              cursor={{ stroke: CHART.cursor }}
              isAnimationActive={false}
              content={(props) => {
                if (!props.active || !props.payload || props.payload.length === 0) return null;
                const row = (props.payload as readonly TooltipEntry[])[0]?.payload ?? {};
                const total = typeof row.total === "number" ? row.total : null;
                const plan = typeof row.plan === "number" ? row.plan : null;
                const lines = pools
                  .map((code, index) => ({ code, index, value: row[code] }))
                  .filter((line): line is { code: string; index: number; value: number } => typeof line.value === "number")
                  .reverse();
                return (
                  <ChartPopover title={String(props.label)}>
                    {lines.map((line) => {
                      const note = poolNote?.(line.code) ?? null;
                      return (
                        <p key={line.code} className="m-0 flex items-center justify-between gap-4">
                          <span className="flex items-center gap-1.5">
                            <span
                              aria-hidden="true"
                              className="size-2 rounded-flag"
                              style={{ background: poolColor(line.code, line.index) }}
                            />
                            {line.code}
                            {note && (
                              <span className="rounded-pill bg-red-soft px-1.5 text-red-ink">{note}</span>
                            )}
                          </span>
                          <span className="tabular-nums">{formatValue(line.value)}</span>
                        </p>
                      );
                    })}
                    {(total !== null || plan !== null) && (
                      <div className="mt-1 flex flex-col gap-0.5 border-t border-separator pt-1.5">
                        {total !== null && (
                          <p className="m-0 flex justify-between gap-4 font-semibold">
                            <span>Kiment</span>
                            <span className="tabular-nums">{formatValue(total)}</span>
                          </p>
                        )}
                        {plan !== null && (
                          <p className="m-0 flex justify-between gap-4 text-muted-foreground">
                            <span>Terv</span>
                            <span className="tabular-nums">{formatValue(plan)}</span>
                          </p>
                        )}
                      </div>
                    )}
                  </ChartPopover>
                );
              }}
            />
            {pools.map((code, index) => (
              <Area
                key={code}
                type="monotone"
                dataKey={code}
                stackId="pools"
                stroke={poolColor(code, index)}
                strokeWidth={1.75}
                fill={`url(#${prefix}-${code})`}
                fillOpacity={1}
                connectNulls={false}
                isAnimationActive={animate}
                animationDuration={duration}
                animationEasing="ease-out"
                activeDot={{ r: 3.2, fill: CHART.surface, stroke: poolColor(code, index), strokeWidth: 1.75 }}
                onAnimationEnd={index === pools.length - 1 ? () => setDrawn(true) : undefined}
              />
            ))}
            {hasPlan && (
              <Line
                type="monotone"
                dataKey="plan"
                stroke={CHART.plan}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                strokeLinecap="round"
                dot={false}
                activeDot={{ r: 3.4, fill: CHART.surface, stroke: CHART.plan, strokeWidth: 1.5 }}
                isAnimationActive={false}
              />
            )}
            {today && (
              <ReferenceLine
                x={today.label}
                stroke={CHART.foreground}
                strokeOpacity={0.35}
                strokeDasharray="2 3"
                label={({ viewBox }: { viewBox?: { x?: number; y?: number } }) => (
                  <g transform={`translate(${viewBox?.x ?? 0},${(viewBox?.y ?? 0) - 6})`}>
                    <rect x={-16} y={-9} width={32} height={17} rx={8.5} style={{ fill: CHART.foreground }} />
                    <text
                      x={0}
                      y={3.5}
                      textAnchor="middle"
                      style={{ fill: CHART.surface, fontSize: 11, fontWeight: 600, fontFamily: "var(--emc-font)" }}
                    >
                      Ma
                    </text>
                  </g>
                )}
              />
            )}
            {last && top && typeof last.total === "number" && (
              <ReferenceDot
                x={last.label}
                y={last.total}
                ifOverflow="visible"
                shape={(props: { cx?: number; cy?: number }) => (
                  <g>
                    <circle cx={props.cx} cy={props.cy} r={10} fill={CHART.series} fillOpacity={0.16} />
                    <circle cx={props.cx} cy={props.cy} r={4.8} fill={CHART.surface} stroke={CHART.series} strokeWidth={2.4} />
                  </g>
                )}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <span className="sr-only">
        {last && typeof last.total === "number" ? `Utolsó egész nap (${last.label}): ${formatValue(last.total)}.` : ""}
      </span>
    </figure>
  );
}
